import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { createPortal } from "react-dom";
import { FaTimes } from "react-icons/fa";
import InvoiceSearchPanel from "./InvoiceSearchPanel";

/**
 * Drawer tìm hóa đơn doc-platform, mở từ bảng Tài liệu trong form.
 *
 * Thay cho màn hình "Step 1 — Select invoice" cũ: người lập phiếu ở luôn trong
 * form, mở drawer, thêm bao nhiêu hóa đơn tùy ý (danh sách không đóng sau mỗi
 * lần Add), rồi đóng lại. Mỗi hóa đơn thêm vào vừa là một dòng trong bảng Tài
 * liệu, vừa tự sinh một dòng chi phí.
 *
 * Props: { show, onClose, onPreview, onAdd, addedIds: Set, activeId,
 *          resolveDetail(documentId) => Promise<invoice> — dùng chung cache với Add }
 */
const InvoiceSearchDrawer = ({ show, onClose, onPreview, onAdd, addedIds, activeId, resolveDetail, paymentRequestId }) => {
  const panelRef = useRef(null);
  const openerRef = useRef(null);

  // onClose đổi identity mỗi lần component cha render. Giữ trong ref để effect chỉ
  // phụ thuộc `show` — nếu không, cleanup (trả focus) chạy lại sau mỗi render và
  // giật focus khỏi ô tìm kiếm khi người dùng đang gõ.
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);

  // Escape đóng drawer; nhớ phần tử đang focus để trả về sau khi đóng.
  useEffect(() => {
    if (!show) return undefined;
    openerRef.current = document.activeElement;
    const onKey = (e) => { if (e.key === "Escape") { e.stopPropagation(); onCloseRef.current?.(); } };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      if (openerRef.current instanceof HTMLElement) openerRef.current.focus();
    };
  }, [show]);

  // Khóa scroll nền và đưa focus vào panel (bàn phím không lạc ra sau lớp phủ).
  useEffect(() => {
    if (!show) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const t = setTimeout(() => panelRef.current?.focus(), 0);
    return () => { document.body.style.overflow = prev; clearTimeout(t); };
  }, [show]);

  if (!show) return null;

  return createPortal(
    <div className="geist-scope tw-fixed tw-inset-0 tw-z-[100050] tw-flex tw-justify-end">
      {/* Nền mờ — click ra ngoài để đóng */}
      <div role="button" tabIndex={-1} aria-label="Close" onClick={onClose}
        className="tw-absolute tw-inset-0 tw-bg-black/30 tw-cursor-default" />

      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Add invoice from Doc Search"
        tabIndex={-1}
        className="tw-relative tw-w-full sm:tw-w-[460px] tw-h-full tw-outline-none tw-bg-geist-bg tw-border-0 tw-border-l tw-border-solid tw-border-geist-border tw-shadow-geist-card tw-flex tw-flex-col tw-animate-fade-in-up">
        <div className="tw-flex tw-items-start tw-justify-between tw-gap-2 tw-px-4 tw-py-3 tw-border-0 tw-border-b tw-border-solid tw-border-geist-border tw-flex-shrink-0">
          <div>
            <div className="tw-text-[15px] tw-font-semibold tw-text-geist-fg">Add invoice from Doc Search</div>
            <p className="tw-text-[13px] tw-text-geist-fg-3 tw-m-0 tw-mt-1">
              View opens the invoice full screen · Add attaches it and creates a cost line.
            </p>
          </div>
          <button type="button" onClick={onClose} title="Close" aria-label="Close"
            className="tw-inline-flex tw-h-9 tw-w-9 tw-items-center tw-justify-center tw-rounded-geist-sm tw-border-0 tw-bg-transparent tw-text-geist-fg-2 hover:tw-bg-geist-100 tw-cursor-pointer tw-flex-shrink-0">
            <FaTimes size={16} />
          </button>
        </div>

        <div className="tw-flex-1 tw-min-h-0 tw-p-3">
          <InvoiceSearchPanel
            paymentRequestId={paymentRequestId}
            onPreview={onPreview}
            onAdd={onAdd}
            addedIds={addedIds}
            activeId={activeId}
            resolveDetail={resolveDetail}
          />
        </div>

        <div className="tw-px-4 tw-py-3 tw-border-0 tw-border-t tw-border-solid tw-border-geist-border tw-flex-shrink-0 tw-text-[13px] tw-text-geist-fg-3">
          Press <span className="tw-font-semibold tw-text-geist-fg-2">Esc</span> to close.
        </div>
      </div>
    </div>,
    document.body
  );
};

InvoiceSearchDrawer.propTypes = {
  show: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onPreview: PropTypes.func,
  onAdd: PropTypes.func,
  addedIds: PropTypes.oneOfType([PropTypes.instanceOf(Set), PropTypes.array]),
  activeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  resolveDetail: PropTypes.func,
  paymentRequestId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default InvoiceSearchDrawer;
